<script lang="ts">
	import type { ProductDetailProps } from "./ProductDetailProps.js";
	import Layout from "../components/Layout.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import ProductMobileGallery from "../components/Product/ProductMobileGallery.svelte";
	import { getContext, onMount, setContext } from "svelte";
	import { descriptionContextKey } from "../contexts/descriptionContextKey.js";
	import { nbspify } from "../../../core/utils/nbspify.js";
	import ProductDesktopGallery from "../components/Product/ProductDesktopGallery.svelte";
	import ProductAllergens from "../components/Product/ProductAllergens.svelte";
	import ProductCartForm from "../components/Product/ProductCartForm.svelte";
	import ProductStructuredData from "../components/Product/ProductStructuredData.svelte";
	import WeddingsBanner from "../components/Wedding/WeddingsBanner.svelte";
	import Branches from "../components/Branches.svelte";
	import RelatedProducts from "../components/Product/RelatedProducts.svelte";
	import logo from "../../../core/assets/logo.png";
	import ProductPriorityLabel from "../components/Product/ProductPriorityLabel.svelte";
	import ProductFiltrationLabel from "../components/Product/ProductFiltrationLabel.svelte";
	import Content from "../components/Content.svelte";
	import { thumbnailImageContextKey } from "../contexts/thumbnailImageContextKey.js";
	import DynamicPicture from "../components/Picture/DynamicPicture.svelte";
	import StaticPicture from "../components/Picture/StaticPicture.svelte";
	import { appName } from "../../../core/schema/names/appName.js";
	import { createPromiseStore } from "../../../core/utils/createPromiseStore.js";
	import type { Inquiry } from "../../../core/schema/Inquiry.js";
	import { getClients } from "../appsync/getClients.js";
	import getWeddingInquiryQuery from "../appsync/queries/getWeddingInquiry.graphql";
	import Preloader from "../components/Preloader.svelte";
	import { canBuyFillings } from "../../../core/schema/utils/canBuyFillings.js";
	import Modal from "../components/Modal.svelte";
	import Button from "../components/Button.svelte";
	import HowItWorks from "../components/HowItWorks.svelte";
	import { christmasFaq } from "../data/christmasFaqs.js";
	import FrequentlyAskedQuestions from "../components/FrequentlyAskedQuestions.svelte";
	import { christmasSteps } from "../data/christmasSteps.js";
	import type { ProductSize } from "../../../core/schema/ProductSize.js";
	import { getImagesForProduct } from "../../../core/schema/utils/getImagesForProduct.js";
	import { getDefaultSizeForProductForCustomer } from "../../../core/schema/utils/getDefaultSizeForProductForCustomer.js";
	import { ProductOption } from "../../../core/schema/ProductOption.js";
	import { isEnum } from "../../../core/utils/enum/isEnum.js";
	import { isInquiryOfType } from "../../../core/schema/utils/isInquiryOfType.js";
	import { InquiryType } from "../../../core/schema/InquiryType.js";

	type $$Props = ProductDetailProps;
	const { maxProductsPerCategory, plannedIceCreams, product, productCategories, products, requiredCategories } =
		$$restProps as $$Props;

	const globalSettings = getGlobalSettings();
	const { TENANT_LOCALE, STORAGE_URL } = globalSettings;
	let isExpress = false;
	$: priorityLabel = isExpress ? ("express" as const) : product.priorityLabel;

	let selectedSize: ProductSize;
	$: images = getImagesForProduct(product, selectedSize);

	const [thumbnailImage] = getImagesForProduct(product, getDefaultSizeForProductForCustomer(product));
	setContext(thumbnailImageContextKey, thumbnailImage ? `${STORAGE_URL}/${thumbnailImage.path}` : logo);

	const description = getContext<string>(descriptionContextKey);

	// TODO SD-219
	// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
	$: firstCategory = productCategories[0]!;
	$: isChristmasCategory = productCategories.some((category) => category.id === requiredCategories.christmas.id);

	const getWeddingInquiry = createPromiseStore(async (id: Inquiry["id"]) => {
		const { graphQL } = await getClients(globalSettings);
		const weddingInquiry = await graphQL.query<Inquiry, { id: Inquiry["id"] }>({
			query: getWeddingInquiryQuery,
			variables: {
				id,
			},
		});

		return weddingInquiry && isInquiryOfType(weddingInquiry, InquiryType.Wedding) && canBuyFillings(weddingInquiry);
	});

	let enabledOption: ProductOption | undefined;

	onMount(async () => {
		if (product.categoryIds.includes(requiredCategories.fillings.id)) {
			const weddingInquiryId = localStorage.getItem("weddingInquiryId");

			if (!weddingInquiryId) {
				window.location.href = "/";
			} else {
				const response = await getWeddingInquiry.call(weddingInquiryId);
				if (!response) {
					window.location.href = "/";
				}
			}
		}

		const urlParams = new URLSearchParams(window.location.search);
		const filterValue = urlParams.get("option");

		if (isEnum(ProductOption)(filterValue)) {
			enabledOption = filterValue;
		}
	});

	let showMoreModal = false;
	// TODO Remove this after Svelte + ESLint are better friends https://github.com/sveltejs/svelte-eslint-parser/issues/306#issuecomment-1938748431
	/* eslint-disable @typescript-eslint/no-unsafe-argument, svelte/no-at-html-tags */
</script>

<ProductStructuredData {product} productCategory={firstCategory} {description} />

<Layout>
	{#if firstCategory.id === requiredCategories.fillings.id && ($getWeddingInquiry.status !== "success" || !$getWeddingInquiry.result)}
		<Preloader />
	{:else}
		<section>
			<div class="container">
				<h1
					class="mb-8 mt-5 px-4 py-0 text-[1.5rem] font-semibold leading-8 md:!hidden md:text-xl md:leading-[3.25rem]"
				>
					{nbspify(product.name[TENANT_LOCALE])}
				</h1>
			</div>
			{#if images.length === 0}
				<div class="container md:hidden">
					<div
						class="bg-secondary-40 flex aspect-square h-auto max-h-[37rem] w-full max-w-[37rem] items-center justify-center rounded-md"
					>
						<StaticPicture
							alt="Logo {appName}"
							image={logo}
							class="w-28 opacity-50 sm:w-36"
							loading="eager"
							width={768}
							height={768}
						/>
					</div>
				</div>
			{:else if images.length > 1}
				<ProductMobileGallery {product} {images} class="mb-8 md:hidden" />
			{:else}
				{@const [primaryImage] = images}
				{#if primaryImage}
					<div class="container md:hidden">
						<DynamicPicture
							imgClass="rounded-md"
							image={primaryImage}
							width={768}
							height={768}
							alt="Fotografie – {product.name[TENANT_LOCALE]}"
							lightboxId={product.id}
							lightboxWidth={800}
							bucket="storage"
							loading="eager"
						/>
					</div>
				{/if}
			{/if}
			<div class="container">
				<div class="mt-4 flex flex-col gap-28">
					<div class="flex flex-col md:grid md:grid-cols-2 md:gap-10">
						<ProductDesktopGallery {product} {images} class="hidden md:block" />

						<div>
							<div class="mb-8 flex flex-col items-start gap-3 md:gap-4">
								{#if priorityLabel !== undefined}
									<ProductPriorityLabel class="text-xs font-medium" label={priorityLabel} withoutBackground />
								{/if}

								<h1
									class="m-0 hidden p-0 text-[1.5rem] font-semibold leading-8 md:inline-block md:text-xl md:leading-[3.25rem]"
								>
									{product.name[TENANT_LOCALE]}
								</h1>

								{#if product.filtrationLabels[TENANT_LOCALE].length > 0}
									<div class="flex flex-wrap items-center gap-1.5">
										{#each product.filtrationLabels[TENANT_LOCALE] as label}
											<ProductFiltrationLabel>{label}</ProductFiltrationLabel>
										{/each}
									</div>
								{/if}
							</div>

							<div class="mt-6">
								<Content content={product.description[TENANT_LOCALE]} />
								{#if product.ingredients}
									<Button
										variant="transparent"
										class="hover:text-primary group inline-block cursor-pointer text-black underline decoration-black transition-[text-decoration,color] duration-300 hover:no-underline"
										on:click={() => {
											showMoreModal = true;
										}}
									>
										Chci vědět více
									</Button>

									<Modal bind:show={showMoreModal}>
										<Content content={product.ingredients[TENANT_LOCALE]} />
									</Modal>
								{/if}
							</div>

							<ProductAllergens allergens={product.allergens} />

							<ProductCartForm bind:isExpress bind:selectedSize {enabledOption} {product} {maxProductsPerCategory} />
						</div>
					</div>
				</div>
			</div>
		</section>
	{/if}

	<div class="container">
		{#if isChristmasCategory}
			<section>
				<HowItWorks steps={christmasSteps} />
			</section>
		{/if}

		{#if products.length > 0}
			<section>
				<RelatedProducts headline="Mohlo by vás zajímat" {products} category={firstCategory} {maxProductsPerCategory} />
			</section>
		{/if}

		{#if !isChristmasCategory}
			<section>
				<HowItWorks />
				<WeddingsBanner />
			</section>
		{/if}

		{#if isChristmasCategory}
			<div class="container">
				<div class="mt-6 flex flex-col items-center gap-8 md:mt-10">
					<span class="md:text-md text-center text-base font-semibold uppercase leading-7 tracking-[0.9375rem]">
						Máte otázku?
					</span>
					<h1
						class="m-0 p-0 text-center text-[1.8rem] font-semibold leading-10 md:text-2xl lg:mt-0 lg:leading-[4.25rem]"
					>
						{nbspify("Odpovědi na časté dotazy")}
					</h1>
				</div>

				<FrequentlyAskedQuestions questionAndAnswers={christmasFaq} />
			</div>
		{/if}

		<section>
			<Branches {plannedIceCreams} />
		</section>
	</div>
</Layout>
